import { Injectable, effect, signal, computed } from '@angular/core';
import { ConfirmationService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { AuthService } from '@wayout-admin/util';
import { Router } from '@angular/router';

export type ColorScheme = 'light' | 'dark' | 'dim';

export interface layoutConfig {
	inputStyle: string;
	preset?: string;
	primary?: string;
	surface?: string | undefined | null;
	ripple: boolean;
	darkTheme?: boolean;
	menuMode?: string;
	menuTheme?: string;
	colorScheme?: ColorScheme;
}

interface LayoutState {
	staticMenuDesktopInactive?: boolean;
	overlayMenuActive?: boolean;
	configSidebarVisible: boolean;
	staticMenuMobileActive?: boolean;
	menuHoverActive?: boolean;
	profileSidebarVisible: boolean;
	sidebarActive: boolean;
	anchored: boolean;
	overlaySubmenuActive: boolean;
	activeMenuItem: any;
}

interface MenuChangeEvent {
	key: string;
	routeEvent?: boolean;
}

@Injectable()
export class LayoutService {
	_config: layoutConfig = {
		ripple: false,
		preset: 'Aura',
		primary: 'indigo',
		inputStyle: 'outlined',
		surface: null,
		darkTheme: false,
		menuMode: 'static',
		menuTheme: 'colorScheme',
	};

	_state: LayoutState = {
		staticMenuDesktopInactive: false,
		overlayMenuActive: false,
		sidebarActive: false,
		anchored: false,
		overlaySubmenuActive: false,
		profileSidebarVisible: false,
		configSidebarVisible: false,
		staticMenuMobileActive: false,
		menuHoverActive: false,
		activeMenuItem: null,
	};

	layoutConfig = signal<layoutConfig>(this._config);

	layoutState = signal<LayoutState>(this._state);

	private configUpdate = new Subject<layoutConfig>();

	private overlayOpen = new Subject<any>();

	private menuSource = new Subject<MenuChangeEvent>();

	private resetSource = new Subject();

	menuSource$ = this.menuSource.asObservable();

	resetSource$ = this.resetSource.asObservable();

	configUpdate$ = this.configUpdate.asObservable();

	overlayOpen$ = this.overlayOpen.asObservable();

	isDarkTheme = computed(() => this.layoutConfig().darkTheme);

	isSlim = computed(() => this.layoutConfig().menuMode === 'slim');

	isSlimPlus = computed(() => this.layoutConfig().menuMode === 'slim-plus');

	isHorizontal = computed(() => this.layoutConfig().menuMode === 'horizontal');

	isOverlay = computed(() => this.layoutConfig().menuMode === 'overlay');

	transitionComplete = signal<boolean>(false);

	isSidebarStateChanged = computed(() => {
		const layoutConfig = this.layoutConfig();
		return (
			layoutConfig.menuMode === 'horizontal' ||
			layoutConfig.menuMode === 'slim' ||
			layoutConfig.menuMode === 'slim-plus'
		);
	});

	private initialized = false;

	constructor(
		private confirmationService: ConfirmationService,
		private translationService: TranslateService,
		private authService: AuthService,
		private router: Router
	) {
		effect(() => {
			const config = this.layoutConfig();
			if (config) {
				this.onConfigUpdate();
			}
		});

		effect(() => {
			const config = this.layoutConfig();

			if (!this.initialized || !config) {
				this.initialized = true;
				return;
			}

			this.handleDarkModeTransition(config);
		});

		effect(() => {
			this.isSidebarStateChanged() && this.reset();
		});
	}

	private handleDarkModeTransition(config: layoutConfig): void {
		const supportsViewTransition = 'startViewTransition' in document;

		if (supportsViewTransition) {
			this.startViewTransition(config);
		} else {
			this.toggleDarkMode(config);
			this.onTransitionEnd();
		}
	}

	private startViewTransition(config: layoutConfig): void {
		const transition = (document as any).startViewTransition(() => {
			this.toggleDarkMode(config);
		});

		transition.ready
			.then(() => {
				this.onTransitionEnd();
			})
			.catch(() => {});
	}

	toggleDarkMode(config?: layoutConfig): void {
		const _config = config || this.layoutConfig();
		if (_config.darkTheme) {
			document.documentElement.classList.add('app-dark');
		} else {
			document.documentElement.classList.remove('app-dark');
		}
	}

	private onTransitionEnd() {
		this.transitionComplete.set(true);
		setTimeout(() => {
			this.transitionComplete.set(false);
		});
	}

	onMenuToggle() {
		if (this.isOverlay()) {
			this.layoutState.update((prev) => ({ ...prev, overlayMenuActive: !this.layoutState().overlayMenuActive }));

			if (this.layoutState().overlayMenuActive) {
				this.overlayOpen.next(null);
			}
		}

		if (this.isDesktop()) {
			this.layoutState.update((prev) => ({
				...prev,
				staticMenuDesktopInactive: !this.layoutState().staticMenuDesktopInactive,
			}));
		} else {
			this.layoutState.update((prev) => ({
				...prev,
				staticMenuMobileActive: !this.layoutState().staticMenuMobileActive,
			}));

			if (this.layoutState().staticMenuMobileActive) {
				this.overlayOpen.next(null);
			}
		}
	}
	onConfigUpdate() {
		this._config = { ...this.layoutConfig() };
		this.configUpdate.next(this.layoutConfig());
	}

	onMenuStateChange(event: MenuChangeEvent) {
		this.menuSource.next(event);
	}

	reset() {
		this.resetSource.next(true);
	}

	onOverlaySubmenuOpen() {
		this.overlayOpen.next(null);
	}

	showProfileSidebar() {
		this.layoutState.update((state) => ({
			...state,
			profileSidebarVisible: true,
		}));
	}

	showConfigSidebar() {
		this.layoutState.update((state) => ({
			...state,
			configSidebarVisible: true,
		}));
	}

	hideConfigSidebar() {
		this.layoutState.update((prev) => ({ ...prev, configSidebarVisible: false }));
	}

	isDesktop() {
		return window.innerWidth > 991;
	}

	isMobile() {
		return !this.isDesktop();
	}

	onLogOut(event: Event) {
		this.confirmationService.confirm({
			target: event.target as EventTarget,
			message: this.translationService.instant('logout.confirmation.text'),
			header: this.translationService.instant('logout.confirmation.header'),
			closable: true,
			closeOnEscape: true,
			icon: 'pi pi-exclamation-triangle',
			key: 'logout',
			rejectButtonProps: {
				label: this.translationService.instant('shared.cancel'),
				severity: 'secondary',
				outlined: true,
			},
			acceptButtonProps: {
				label: this.translationService.instant('shared.yes'),
			},
			accept: () => {
				this.authService.removeUser();
				this.router.navigate(['/auth']);
			},
		});
	}
}
